import { getContrastRatio, useTheme } from '@mui/material/styles';
import { useEffect, useId, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import CloseRounded from '@mui/icons-material/CloseRounded';
import DescriptionRounded from '@mui/icons-material/DescriptionRounded';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import type { CompiledSurvey } from '@apruv/survey-contract/compiler';
import type { CXPublicSurveyResponse } from '../../services/cxPublicSurveyService';
import HtmlText from '../components/HtmlText';
import { sanitizeSurveyHtml } from '../sanitizeHtml';
import { resolveSurveyTemplate } from '../customerSurveyFlow';

interface Props {
  compiled: CompiledSurvey;
  response: CXPublicSurveyResponse;
  logoUrl?: string;
  logoLabel: string;
  disabled?: boolean;
  externalAllowed: boolean;
  locale?: string;
  hideNotesAction?: boolean;
}

// Opaque hex colors can be compared reliably. Other CSS forms get a neutral
// backing on text controls, so transparency/variables cannot erase their labels.
export function headerContrast(background: string) {
  if (!/^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(background))
    return { color: '#1C252E', backing: '#FFFFFF' };
  return {
    color:
      getContrastRatio(background, '#1C252E') >= 4.5
        ? '#1C252E'
        : getContrastRatio(background, '#FFFFFF') >= 4.5
          ? '#FFFFFF'
          : '#000000',
    backing: 'transparent',
  };
}

/** The phone and notes are invocation-owned API data, never author-supplied contact values. */
export function FeedbackSurveyHeader({
  compiled,
  response,
  logoUrl,
  logoLabel,
  disabled,
  externalAllowed,
  locale,
  hideNotesAction,
}: Props) {
  const theme = useTheme();
  const config = compiled.snapshot.configuration;
  const { header, notes, localization } = config;
  const contrast = headerContrast(
    header.background === 'surface'
      ? config.tokens.surface
      : header.background === 'background'
        ? config.tokens.background
        : header.background
  );
  const [open, setOpen] = useState(false);
  const [failedLogo, setFailedLogo] = useState(false);
  const titleId = useId();
  const notesId = useId();
  const local = (key: string, fallback: string) =>
    (locale && localization.supportedLocales.includes(locale)
      ? localization.translations[locale]?.[key]
      : undefined) ??
    localization.translations[localization.defaultLocale]?.[key] ??
    fallback;
  const notesLabel = local('configuration/header/notesLabel', header.notesLabel);
  const contactLabel = local('configuration/header/contactLabel', header.contactLabel);
  const brandLabel = logoLabel.replace(/\s+/g, ' ').trim() || 'Your service provider';
  const title = resolveSurveyTemplate(
    local('configuration/notes/title', notes.title),
    response,
    config.branding,
    config.content.variableFallbacks
  );
  const emptyText = local('configuration/notes/emptyText', notes.emptyText);
  const closeLabel =
    local('configuration/gallery/closeLabel', config.gallery.closeLabel).trim() ||
    localization.translations[localization.defaultLocale]?.['configuration/gallery/closeLabel']?.trim() ||
    config.gallery.closeLabel.trim() ||
    'Close';
  const rawNotes = response.runtimeContext?.technicianNotes ?? '';
  const noteText = notes.format === 'sanitized_rich_text' ? sanitizeSurveyHtml(rawNotes) : rawNotes;
  const hasNotes =
    (notes.format === 'sanitized_rich_text'
      ? noteText.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ')
      : noteText
    ).trim().length > 0;
  const showNotes =
    !hideNotesAction &&
    header.actions.includes('notes') &&
    (hasNotes || notes.emptyBehavior === 'message');
  const rawPhone =
    header.phoneReference === 'organization.phone' &&
    response.runtimeContext?.capabilities?.canContact &&
    typeof response.runtimeContext.contactPhone === 'string'
      ? response.runtimeContext.contactPhone.trim()
      : '';
  const dialable = /^\+?[\d\s().-]+$/.test(rawPhone) ? rawPhone.replace(/[^\d+]/g, '') : '';
  const showContact = header.actions.includes('contact') && /\d/.test(dialable);
  useEffect(() => setFailedLogo(false), [logoUrl]);
  useEffect(() => setOpen(false), [compiled, response.appointmentCode]);
  useEffect(() => {
    if (!showNotes) setOpen(false);
  }, [showNotes]);
  return (
    <>
      <Box
        data-testid="feedback-header-actions"
        sx={{
          display: 'grid',
          gridTemplateColumns:
            header.alignment === 'center'
              ? 'repeat(3, minmax(0, 1fr))'
              : 'minmax(0, 1fr) auto',
          '@media (max-width: 360px)':
            header.alignment === 'center' ? { gridTemplateColumns: 'minmax(0, 1fr)' } : {},
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          minWidth: 0,
          minHeight: 36,
        }}
      >
        <Box
          sx={{
            minWidth: 0,
            gridColumn: header.alignment === 'center' ? 2 : undefined,
            '@media (max-width: 360px)': header.alignment === 'center' ? { gridColumn: 1 } : {},
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            justifyContent: header.alignment === 'center' ? 'center' : 'flex-start',
          }}
        >
          {logoUrl && !failedLogo ? (
            <Box
              component="img"
              alt={brandLabel}
              src={logoUrl}
              onError={() => setFailedLogo(true)}
              sx={{
                display: 'block',
                height: header.logoSize,
                maxWidth: '100%',
                objectFit: 'contain',
              }}
            />
          ) : (
            <Typography
              title={brandLabel}
              sx={{
                fontWeight: 800,
                fontSize: 16,
                lineHeight: 1.2,
                letterSpacing: '-0.01em',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                color: contrast.color,
                bgcolor: contrast.backing,
              }}
            >
              {brandLabel}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gridColumn: header.alignment === 'center' ? 3 : undefined,
            '@media (max-width: 360px)':
              header.alignment === 'center' ? { gridColumn: 1, justifyContent: 'center' } : {},
            minWidth: 0,
            flexWrap: 'nowrap',
            gap: 1,
            flexShrink: 0,
          }}
        >
          {header.actions.map((action) =>
            action === 'notes'
              ? showNotes && (
                  <Button
                    key="notes"
                    aria-haspopup="dialog"
                    aria-expanded={open}
                    aria-controls={open ? notesId : undefined}
                    aria-label={notesLabel}
                    disabled={disabled}
                    onClick={() => setOpen(true)}
                    startIcon={<DescriptionRounded aria-hidden fontSize="small" />}
                    sx={{
                      minWidth: 36,
                      minHeight: 36,
                      maxWidth: '100%',
                      px: 1,
                      borderRadius: 999,
                      whiteSpace: 'nowrap',
                      color: contrast.color,
                      bgcolor: contrast.backing,
                      border: '1px solid rgba(99, 115, 129, 0.22)',
                      '&:hover': { bgcolor: 'rgba(99, 115, 129, 0.08)' },
                      fontWeight: 600,
                      '& .MuiButton-startIcon': { mr: 0.625 },
                      '@media (max-width: 360px)': {
                        width: 36,
                        minWidth: 36,
                        px: 0,
                        fontSize: 0,
                        '& .MuiButton-startIcon': { m: 0 },
                      },
                    }}
                  >
                    {notesLabel}
                  </Button>
                )
              : showContact && (
                  <IconButton
                    key="contact"
                    component="a"
                    href={externalAllowed && !disabled ? `tel:${dialable}` : undefined}
                    aria-label={contactLabel}
                    aria-disabled={!externalAllowed || disabled}
                    onClick={(event) => {
                      if (!externalAllowed || disabled) event.preventDefault();
                    }}
                    sx={{
                      width: 36,
                      height: 36,
                      bgcolor: 'rgba(28,37,46,0.8)',
                      borderRadius: '50%',
                      border: '1px solid #FFFFFF',
                      '&:hover': { bgcolor: '#1C252E' },
                    }}
                  >
                    <Box
                      component="img"
                      src="/assets/survey/phone-bold.svg"
                      alt=""
                      sx={{ width: 20, height: 20 }}
                    />
                  </IconButton>
                )
          )}
        </Box>
      </Box>
      <Dialog
        dir={localization.direction}
        open={open && showNotes}
        onClose={() => setOpen(false)}
        aria-labelledby={titleId}
        id={notesId}
        maxWidth={false}
        slotProps={{
          container: { sx: { alignItems: 'flex-end' } },
          paper: {
            sx: {
              fontFamily: theme.typography.fontFamily,
              '& .MuiTypography-root, & .MuiButtonBase-root': { fontFamily: theme.typography.fontFamily },
              m: 0,
              width: '100%',
              maxWidth: 444,
              maxHeight: '80dvh',
              borderRadius: '16px 16px 0 0',
              color: config.tokens.text,
              bgcolor: config.tokens.surface,
            },
          },
          backdrop: { sx: { bgcolor: 'rgba(0,0,0,0.48)' } },
        }}
      >
        <Box
          aria-hidden
          sx={{
            width: 32,
            height: 4,
            minHeight: 4,
            mx: 'auto',
            mt: '4px',
            borderRadius: '30px',
            bgcolor: 'rgba(145,158,171,0.32)',
          }}
        />
        <DialogTitle
          id={titleId}
          sx={{
            color: 'inherit',
            fontSize: 17,
            lineHeight: '26px',
            fontWeight: 600,
            textAlign: 'center',
            px: 6,
            pt: 1,
            pb: 2,
          }}
        >
          {title}
        </DialogTitle>
        <IconButton
          aria-label={closeLabel}
          onClick={() => setOpen(false)}
          sx={{ position: 'absolute', insetInlineEnd: 4, top: 8, width: 44, height: 44, color: config.tokens.text }}
        >
          <CloseRounded sx={{ fontSize: 20 }} />
        </IconButton>
        <DialogContent sx={{ px: 2, pt: 0, pb: 2, minHeight: 0, overscrollBehavior: 'contain' }}>
          <Box
            sx={{
              fontFamily: theme.typography.fontFamily,
              border: '1px solid rgba(145,158,171,0.2)',
              borderRadius: '8px',
              px: '14px',
              py: 2,
              minHeight: 98,
              overflowWrap: 'anywhere',
              fontSize: 15,
              lineHeight: '24px',
            }}
          >
            {hasNotes && notes.format === 'sanitized_rich_text' ? (
              <HtmlText html={noteText} component="div" sx={{ font: 'inherit', '& :not(code, pre, kbd, samp, code *, pre *, kbd *, samp *)': { fontFamily: theme.typography.fontFamily }, '& ul': { listStyleType: 'disc' }, '& ol': { listStyleType: 'decimal' }, '& p, & h1, & h2, & h3, & h4, & h5, & h6, & li': { color: 'inherit' } }} />
            ) : (
              <Typography component="div" sx={{ font: 'inherit', whiteSpace: 'pre-wrap' }}>
                {hasNotes ? noteText : emptyText}
              </Typography>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
